import { useEffect, useState } from "react"

import { sortByKey } from "web/services/helpers"
import { useTopicIdentityFields } from "web/hooks/data"
import { ContentBox, InfoMessage } from "web/components"
import { Label, Select, Radio } from "web/components/Form/Controls"
import { DeleteIconButton } from "web/components/IconButton"

function FieldListItem({ fieldListItem, readOnly }) {
  const REQUIRED_VALUE = "required"
  const OPTIONAL_VALUE = "optional"

  const { id, field } = fieldListItem

  function handleDeleteField(field) {
    return field.destroy()
  }

  function handleIsRequiredChanged(value) {
    return fieldListItem.update({ is_required: value.value === REQUIRED_VALUE })
  }

  return (
    <div
      key={id}
      className="mt-2 flex items-center gap-x-2.5 rounded-md border px-2.5 py-1 text-sm shadow-sm"
    >
      <div className="flex-auto font-medium">{field.label}</div>
      <Radio
        lookup_values={[
          { value: REQUIRED_VALUE, label: "Required" },
          { value: OPTIONAL_VALUE, label: "Optional" },
        ]}
        value={{
          value: fieldListItem.is_required ? REQUIRED_VALUE : OPTIONAL_VALUE,
        }}
        onChange={handleIsRequiredChanged}
        disabled={readOnly}
      />

      {!readOnly && (
        <DeleteIconButton onClick={() => handleDeleteField(fieldListItem)} />
      )}
    </div>
  )
}

export default function TopicIdentityFieldList({
  topicId,
  fields,
  readOnly = false,
}) {
  const requiredFields = useTopicIdentityFields({ topicId: topicId })

  const [, setLayoutFields] = useState(sortByKey(requiredFields, "ordinal"))

  useEffect(() => {
    setLayoutFields(sortByKey(requiredFields, "ordinal"))
  }, [requiredFields])

  function handleNewFieldSelected(value) {
    if (value === null) return

    return requiredFields.add({ field_id: value.value })
  }

  function getSelectOptionsFromFields() {
    const requiredFieldIds = requiredFields.map((f) => f.field.id)

    const selectOptions = fields
      .filter((field) => !requiredFieldIds.includes(field.id))
      .map((field) => ({
        value: field.id,
        label: field.label,
      }))

    return sortByKey(selectOptions, "label")
  }

  return (
    <ContentBox className="edit-field-list border-0 shadow-none">
      {readOnly && (
        <InfoMessage title="Fields Locked">
          Standard topic identity fields are not editable.
        </InfoMessage>
      )}

      {requiredFields.map((field) => (
        <div key={field.id} className="mt-2">
          <FieldListItem fieldListItem={field} readOnly={readOnly} />
        </div>
      ))}

      {requiredFields.length === 0 && (
        <div className="text-center">
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No fields selected
          </h3>
        </div>
      )}

      {!readOnly && (
        <div className="mt-5">
          <Label label="Select Field to Add" />
          <Select
            id="field-to-add"
            testId="select-field-to-add"
            value={null}
            onChange={handleNewFieldSelected}
            options={getSelectOptionsFromFields()}
          />
        </div>
      )}
    </ContentBox>
  )
}
